import { Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FlashcardService } from 'src/app/core/flashcard.service';
import { LearnsetService } from 'src/app/core/learnset.service';
import { Flashcard } from 'src/app/models/flashcard.model';
import { Learnset } from 'src/app/models/learnset.model';

declare var webkitSpeechRecognition: any;
declare var SpeechRecognition: any;

@Component({
  selector: 'app-learn-audio',
  templateUrl: './learn-audio.component.html',
  styleUrls: ['./learn-audio.component.scss'],
  standalone: false
})
export class LearnAudioComponent implements OnInit {

  recognition!: any;
  recognizedText = '';
  isRecording = false;

  learnset?: Learnset;
  stack?: Flashcard[];
  currentFlashcard?: Flashcard;
  currentFlashcardSide: 'front' | 'back' = 'front';
  totalCards: number = 0;
  cardsProgress: number = 0;
  cumulativeScore: number = 0;
  learnedCards: number = 0;
  showFeedback: boolean = false;
  feedbackTitle?: string;
  feedbackMessage?: string;
  feedbackScore?: number;
  feedbackColor?: 'success' | 'warn' | 'error';
  allCardsFinished: boolean = false;

  isLoadingFeedback: boolean = false;

  text: string = '';

  constructor(
    private learnsetService: LearnsetService,
    private flashcardService: FlashcardService,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    const learnsetId = this.route.snapshot.paramMap.get('id');
    this.fetchLearnset(learnsetId!);
    this.initSpeechRecognition();
  }

  initSpeechRecognition(): void {
    const SpeechRecognitionConstructor = SpeechRecognition || webkitSpeechRecognition;
    if (!SpeechRecognitionConstructor) {
      alert('Speech Recognition wird von deinem Browser nicht unterstützt.');
      return;
    }

    this.recognition = new SpeechRecognitionConstructor();
    this.recognition.lang = 'de-DE';
    this.recognition.interimResults = false;
    this.recognition.maxAlternatives = 1;

    this.recognition.onresult = (event: any) => {
      this.ngZone.run(() => {
        this.recognizedText = event.results[0][0].transcript;
        this.evaluateAnswer(this.recognizedText); // ruft intern submitAnswer auf
      });
    };

    this.recognition.onerror = (event: any) => {
      console.error('STT Fehler:', event);
      this.isRecording = false;
    };

    this.recognition.onend = () => this.isRecording = false;
  }

  startRecording(): void {
    if (!this.recognition) return;
    this.recognizedText = '';
    this.isRecording = true;
    this.recognition.start();
  }

  stopRecording(): void {
    if (!this.recognition) return;
    this.recognition.stop();
    this.isRecording = false;
    this.isLoadingFeedback = true;
  }

  evaluateAnswer(text: string): void {
    this.text = text;
    this.submitAnswer();
  }

  fetchLearnset(learnsetId: string): void {
    this.learnsetService.getLearnset(learnsetId)
      .subscribe((learnset: any) => {
        this.learnset = Learnset.constructLearnset(learnset);
        this.stack = JSON.parse(JSON.stringify(this.learnset.flashcards.filter(f => f.dueDate! < new Date())));
        this.totalCards = this.stack!.length;
        this.currentFlashcard = this.stack![0];
      });
  }

  nextCard() {
    this.stack!.splice(0, 1);
    this.currentFlashcard = this.stack![0];
    this.currentFlashcardSide = 'front';
    this.showFeedback = false;
    speechSynthesis.cancel();
  }

  finishSession() {
    this.router.navigate(['/sets/' + this.learnset?.id]);
  }

  getFeedbackColor(score: number): 'success' | 'warn' | 'error' {
    return score >= 4 ? 'success' : 'warn';
  }

  submitAnswer() {
    this.isLoadingFeedback = true;
    this.flashcardService.getAnswerFeedback(this.currentFlashcard!.id!, this.text).subscribe((result: any) => {
      this.learnedCards++;
      this.isLoadingFeedback = false;
      this.currentFlashcardSide = 'back';
      this.showFeedback = true;
      this.text = '';
      this.feedbackTitle = result.feedbackTitle;
      this.feedbackMessage = result.feedbackMessage;
      this.feedbackScore = 1;
      this.feedbackColor = this.getFeedbackColor(result.score);
      this.cardsProgress = (100 / this.totalCards) * this.learnedCards;
      
      if (this.cardsProgress === 100) {
        this.allCardsFinished = true;
      }

      this.cumulativeScore += result.score;
      this.feedbackScore = result.score;
      this.feedbackColor = this.getFeedbackColor(result.score);

      const utterance = new SpeechSynthesisUtterance(result.feedbackMessage);
      speechSynthesis.speak(utterance);
    });
  }
}